import React from "react"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import StatisticsTable from "shared/ui/Table/StatisticTable.component"

import { DEFAULT_TABLE_WIDTH } from "constants/dashboardElementSizes"

import { TransformedTableData } from "utils/convertDataToTable"
import { EngineOutputItem } from "types/engineOutput.types"

/**
 * Props for TableDisplayArea component
 */
export interface TableDisplayAreaProps {
  /** Table data from engine */
  tableData: EngineOutputItem | null
  /** Converted table data ready for rendering */
  convertedTableData: TransformedTableData | null
  /** Error message if any */
  error: string | null
  /** Loading state */
  isLoading: boolean
  /** Queued for execution state */
  isQueuedForExecution: boolean
  /** Whether config is initializing */
  isInitializing: boolean
  /** Whether load has been attempted */
  hasAttemptedLoad: boolean
  /** Element width for scaling */
  elementWidth?: number
  /** Reference to table container */
  tableContainerRef: React.RefObject<HTMLDivElement | null>
  /** Refresh handler */
  onRefresh: () => void
}

/**
 * Table Display Area Component
 *
 * Handles the rendering of table data and all display states:
 * - StatisticsTable rendering with proper scaling
 * - Error state with retry functionality
 * - Loading states (execution and queue)
 * - Empty state when no data available
 * - Responsive container with overflow handling
 *
 * Features:
 * - Content-aware scaling based on element width
 * - Stretch-to-fill layout for optimal space usage
 * - Static table mode (no editing capabilities)
 * - Professional styling with shadows and rounded corners
 * - Accessibility support with proper ARIA labels
 *
 * @param props - Component configuration and data
 * @returns JSX element for table display
 */
const TableDisplayArea: React.FC<TableDisplayAreaProps> = ({
  tableData,
  convertedTableData,
  error,
  isLoading,
  isQueuedForExecution,
  isInitializing,
  hasAttemptedLoad,
  elementWidth = DEFAULT_TABLE_WIDTH,
  tableContainerRef,
  onRefresh,
}) => {
  // Error State
  if (error && !isInitializing) {
    return (
      <div
        className="mt-1 bg-red-50 border border-red-200 text-red-600 px-2 py-1 rounded-sm text-xs shadow-sm"
        role="alert"
      >
        <div className="flex justify-between items-center">
          <p>{error}</p>
          <DataQButton
            onClick={onRefresh}
            size="small"
            className="ml-2"
            type="link"
          >
            Retry
          </DataQButton>
        </div>
      </div>
    )
  }

  // Loading States
  if (isLoading || isQueuedForExecution) {
    return (
      <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-md z-20">
        <div className="text-blue-500 text-sm bg-blue-50 px-3 py-1 rounded-md shadow-sm">
          {isQueuedForExecution ? "Queued for execution..." : "Loading..."}
        </div>
      </div>
    )
  }

  // Table Data Display
  if (tableData && !error && convertedTableData) {
    return (
      <div
        className="bg-gray-100 rounded-md relative mt-1"
        ref={tableContainerRef}
        style={{
          width: "100%",
          height: "auto",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          margin: 0,
          position: "relative",
          minHeight: "auto",
        }}
      >
        <div
          className="w-full flex items-start justify-start"
          style={{ margin: 0, padding: 0 }}
        >
          <StatisticsTable
            transformedData={convertedTableData}
            onUpdateData={() => {}}
            removedElements={{ columns: new Set(), rows: new Set() }}
            setPersistentRows={() => {}}
            isStatic={true}
            contentScale={elementWidth / 1000}
            stretchToFill={true}
          />
        </div>
      </div>
    )
  }

  // Data Processing Error
  if (tableData && !error && !convertedTableData) {
    return <div className="p-4 text-gray-500">Error processing table data.</div>
  }

  // Empty State
  if (
    !tableData &&
    !isLoading &&
    !isQueuedForExecution &&
    !error &&
    !isInitializing &&
    hasAttemptedLoad
  ) {
    return (
      <div className="flex items-center justify-center h-20 bg-gray-50 bg-opacity-80 rounded-md shadow-sm mt-1">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">
            Table data could not be loaded
          </p>
          <DataQButton onClick={onRefresh} type="primary" size="small">
            Retry
          </DataQButton>
        </div>
      </div>
    )
  }

  // Default: No content to display
  return null
}

export default TableDisplayArea
